import { Badge, Card, ErrorAlert, PageHeader, Spinner, Switch, buttonStyles } from '@lumlab/platform-ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { apiRequest, type ProductWeb } from '@lumlab/platform-product/web';
import type { ProductAccess } from '../contract/index.ts';
import { OrganizationScope } from './OrganizationScope.tsx';

export function ProductsPage({ products }: { products: readonly ProductWeb[] }) {
    const { t } = useTranslation();
    return (
        <>
            <PageHeader title={t('platform.admin.products')} description={t('platform.admin.productsDescription')} />
            <OrganizationScope>
                {(organizationId) => <OrganizationProducts organizationId={organizationId} products={products} />}
            </OrganizationScope>
        </>
    );
}

function OrganizationProducts({
    organizationId,
    products,
}: {
    organizationId: string;
    products: readonly ProductWeb[];
}) {
    const { t } = useTranslation();
    const client = useQueryClient();
    const path = `/api/admin/organizations/${organizationId}/products`;
    const grants = useQuery({
        queryKey: ['admin', path],
        queryFn: ({ signal }) => apiRequest<{ products: ProductAccess[] }>(path, { signal }),
    });
    const update = useMutation({
        mutationFn: ({ productId, enabled }: ProductAccess) =>
            apiRequest(`${path}/${productId}`, { method: 'PUT', body: { enabled } }),
        onSuccess: async () => {
            await client.invalidateQueries({ queryKey: ['admin', path] });
            await client.invalidateQueries({ queryKey: ['access'] });
        },
    });
    return (
        <div className="flex flex-col gap-4">
            {grants.isPending && <Spinner label={t('platform.shell.loading')} />}
            {grants.error && <ErrorAlert error={grants.error} />}
            {update.error && <ErrorAlert error={update.error} />}
            {grants.data?.products.map((grant) => (
                <Card key={grant.productId}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="flex flex-col gap-2">
                            <p className="font-display text-lg font-semibold">{t(`${grant.productId}.product.name`)}</p>
                            <p className="text-sm text-muted">{t(`${grant.productId}.product.tagline`)}</p>
                            <Badge tone={grant.enabled ? 'success' : 'neutral'}>
                                {grant.enabled ? t('platform.admin.enabled') : t('platform.admin.disabled')}
                            </Badge>
                        </div>
                        <Switch
                            label={t('platform.admin.productAccess', { product: t(`${grant.productId}.product.name`) })}
                            checked={grant.enabled}
                            disabled={update.isPending}
                            onChange={(event) =>
                                update.mutate({ productId: grant.productId, enabled: event.currentTarget.checked })
                            }
                        />
                    </div>
                    {products.find((product) => product.id === grant.productId)?.administration && (
                        <Link
                            className={buttonStyles({ variant: 'light', className: 'mt-4' })}
                            to={`/admin/products/${grant.productId}?organizationId=${organizationId}`}
                        >
                            {t('platform.admin.openProduct')}
                        </Link>
                    )}
                </Card>
            ))}
        </div>
    );
}
